/**
 * Alert Level Utilities
 * Handles alert filtering and hierarchy based on user preferences
 */

// Define alert level hierarchy (higher number = higher priority)
const ALERT_LEVEL_HIERARCHY = {
  info: 1,
  success: 2,
  warning: 3,
  error: 4,
};

/**
 * Get the numeric priority of an alert level
 * @param {string} level - Alert level (info, warning, error, success)
 * @returns {number} Priority number (1-4, higher = more critical)
 */
export const getAlertLevelPriority = (level) => {
  const normalizedLevel = level?.toLowerCase();
  return ALERT_LEVEL_HIERARCHY[normalizedLevel] || 1;
};

/**
 * Check if an alert should be shown based on user's minimum level preference
 * Warning and Error alerts are ALWAYS shown regardless of user preference
 * @param {string} alertType - The alert's type (INFO, WARNING, ERROR, SUCCESS)
 * @param {string} userMinLevel - User's minimum alert level preference
 * @returns {boolean} True if alert should be shown
 */
export const shouldShowAlert = (alertType, userMinLevel) => {
  if (!alertType) {
    return true; // Show by default if alert type is missing
  }

  const normalizedAlertType = alertType.toLowerCase();
  const normalizedUserLevel = (userMinLevel || "both").toLowerCase();

  // Warning and Error alerts are ALWAYS shown
  if (normalizedAlertType === "warning" || normalizedAlertType === "error") {
    return true;
  }

  // Handle configurable alert types (Info and Success)
  switch (normalizedUserLevel) {
    case "info":
      // Show Info alerts (Success, Warning, Error already handled above)
      return normalizedAlertType === "info";
    case "success":
      // Show Success alerts (Warning, Error already handled above)
      return normalizedAlertType === "success";
    case "both":
      // Show both Info and Success alerts (Warning, Error already handled above)
      return (
        normalizedAlertType === "info" || normalizedAlertType === "success"
      );
    case "none":
      // Show neither Info nor Success (only Warning, Error handled above)
      return false;
    default:
      // Default to showing Info alerts (+ critical alerts always shown)
      return normalizedAlertType === "info";
  }
};

/**
 * Filter an array of alerts based on user's level preference
 * @param {Array} alerts - Array of alert objects
 * @param {string} userMinLevel - User's minimum alert level preference
 * @returns {Array} Filtered array of alerts
 */
export const filterAlertsByLevel = (alerts, userMinLevel) => {
  if (!Array.isArray(alerts) || !userMinLevel) {
    return alerts || [];
  }

  return alerts.filter((alert) => {
    // Handle both 'type' and 'alertType' properties for flexibility
    const alertType = alert.type || alert.alertType;
    return shouldShowAlert(alertType, userMinLevel);
  });
};

/**
 * Get user's alert level preference from localStorage
 * @returns {string} User's preferred alert level (defaults to 'info' - info + critical alerts)
 */
export const getUserAlertLevel = () => {
  return localStorage.getItem("alertLevel") || "info";
};

/**
 * Set user's alert level preference in localStorage
 * @param {string} level - Alert level to save
 */
export const setUserAlertLevel = (level) => {
  localStorage.setItem("alertLevel", level);

  // Dispatch custom event to notify components
  window.dispatchEvent(
    new CustomEvent("alertLevelChanged", {
      detail: { alertLevel: level },
    })
  );
};

/**
 * Get alert level configuration for UI display
 * Warning and Error alerts are always shown - only Info and Success are configurable
 * @returns {Array} Array of alert level options with display properties
 */
export const getAlertLevelOptions = () => [
  {
    value: "both",
    label: "📢 Show All Optional Alerts",
    description: "Shows Info and Success alerts (Warning/Error always shown)",
    priority: 1,
  },
  {
    value: "info",
    label: "📢 Info Only",
    description: "Shows Info alerts only (+ Warning/Error always shown)",
    priority: 2,
  },
  {
    value: "success",
    label: "✅ Success Only",
    description: "Shows Success alerts only (+ Warning/Error always shown)",
    priority: 3,
  },
  {
    value: "none",
    label: "🔕 Hide Optional Alerts",
    description: "Shows only Warning and Error alerts (hide Info/Success)",
    priority: 4,
  },
];

export default {
  getAlertLevelPriority,
  shouldShowAlert,
  filterAlertsByLevel,
  getUserAlertLevel,
  setUserAlertLevel,
  getAlertLevelOptions,
};
